import { useEffect } from "react"
import { useForm } from "react-hook-form"
import { zodResolver } from "@hookform/resolvers/zod"
import { useTranslation } from "react-i18next"
import * as z from "zod"

import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  Button,
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
  Input,
  RadioGroup,
  RadioGroupItem,
} from "@f2e/ui/components/base"
import { ApiKeyExpirationDatePicker } from "./ApiKeyExpirationDatePicker"

const createIssueApiKeySchema = (t: (key: string) => string) =>
  z.object({
    name: z
      .string()
      .min(1, t("workspace.apiKeys.issueDialog.errors.nameRequired"))
      .max(255, t("workspace.apiKeys.issueDialog.errors.nameTooLong")),
    role: z.enum(["member", "admin"]),
    expiresAt: z.date().optional(),
  })

type IssueApiKeyFormData = z.infer<ReturnType<typeof createIssueApiKeySchema>>

export interface IssueApiKeyDialogProps {
  open: boolean
  isSubmitting?: boolean
  onOpenChange: (open: boolean) => void
  onSubmit: (data: IssueApiKeyFormData) => void
}

export const IssueApiKeyDialog = ({
  open,
  isSubmitting = false,
  onOpenChange,
  onSubmit,
}: IssueApiKeyDialogProps) => {
  const { t } = useTranslation()

  const issueApiKeySchema = createIssueApiKeySchema(t)

  const form = useForm<IssueApiKeyFormData>({
    resolver: zodResolver(issueApiKeySchema),
    defaultValues: {
      name: "",
      role: "member",
      expiresAt: undefined,
    },
  })

  const handleSubmit = (data: IssueApiKeyFormData) => {
    onSubmit(data)
  }

  // Reset form when dialog is closed
  useEffect(() => {
    if (!open) {
      form.reset()
    }
  }, [open, form])

  const tomorrow = new Date()
  tomorrow.setDate(tomorrow.getDate() + 1)

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>{t("workspace.apiKeys.issueDialog.title")}</DialogTitle>
          <DialogDescription>
            {t("workspace.apiKeys.issueDialog.description")}
          </DialogDescription>
        </DialogHeader>

        <Form {...form}>
          <form
            onSubmit={form.handleSubmit(handleSubmit)}
            className="space-y-4"
          >
            <FormField
              control={form.control}
              name="name"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>
                    {t("workspace.apiKeys.issueDialog.nameLabel")}
                  </FormLabel>
                  <FormControl>
                    <Input
                      placeholder={t(
                        "workspace.apiKeys.issueDialog.namePlaceholder"
                      )}
                      {...field}
                      disabled={isSubmitting}
                    />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />

            <FormField
              control={form.control}
              name="role"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>
                    {t("workspace.apiKeys.issueDialog.accessLevelLabel")}
                  </FormLabel>
                  <FormControl>
                    <RadioGroup
                      value={field.value}
                      onValueChange={field.onChange}
                      disabled={isSubmitting}
                      className="grid gap-3 sm:grid-cols-2"
                    >
                      <label
                        htmlFor="api-key-role-member"
                        className={`rounded-lg border p-3 transition-colors ${
                          field.value === "member"
                            ? "border-slate-950 bg-slate-50 dark:border-slate-50 dark:bg-slate-900"
                            : "border-slate-200 hover:bg-slate-50 dark:border-slate-800 dark:hover:bg-slate-900"
                        } ${isSubmitting ? "cursor-not-allowed opacity-70" : "cursor-pointer"}`}
                      >
                        <div className="flex items-start gap-3">
                          <RadioGroupItem
                            id="api-key-role-member"
                            value="member"
                            className="mt-1"
                          />
                          <div>
                            <div className="text-sm font-medium">
                              {t(
                                "workspace.apiKeys.issueDialog.accessLevels.member.title"
                              )}
                            </div>
                            <p className="mt-1 text-xs text-slate-500 dark:text-slate-400">
                              {t(
                                "workspace.apiKeys.issueDialog.accessLevels.member.description"
                              )}
                            </p>
                          </div>
                        </div>
                      </label>

                      <label
                        htmlFor="api-key-role-admin"
                        className={`rounded-lg border p-3 transition-colors ${
                          field.value === "admin"
                            ? "border-amber-500 bg-amber-50 dark:border-amber-500 dark:bg-amber-950/30"
                            : "border-slate-200 hover:bg-slate-50 dark:border-slate-800 dark:hover:bg-slate-900"
                        } ${isSubmitting ? "cursor-not-allowed opacity-70" : "cursor-pointer"}`}
                      >
                        <div className="flex items-start gap-3">
                          <RadioGroupItem
                            id="api-key-role-admin"
                            value="admin"
                            className="mt-1"
                          />
                          <div>
                            <div className="text-sm font-medium">
                              {t(
                                "workspace.apiKeys.issueDialog.accessLevels.admin.title"
                              )}
                            </div>
                            <p className="mt-1 text-xs text-slate-500 dark:text-slate-400">
                              {t(
                                "workspace.apiKeys.issueDialog.accessLevels.admin.description"
                              )}
                            </p>
                          </div>
                        </div>
                      </label>
                    </RadioGroup>
                  </FormControl>
                  {field.value === "admin" && (
                    <p className="rounded-md border border-amber-200 bg-amber-50 px-3 py-2 text-xs text-amber-800 dark:border-amber-900 dark:bg-amber-950/40 dark:text-amber-200">
                      {t("workspace.apiKeys.issueDialog.adminWarning")}
                    </p>
                  )}
                  <FormMessage />
                </FormItem>
              )}
            />

            <FormField
              control={form.control}
              name="expiresAt"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>
                    {t("workspace.apiKeys.issueDialog.expirationLabel")}
                  </FormLabel>
                  <FormControl>
                    <ApiKeyExpirationDatePicker
                      date={field.value}
                      onSelect={field.onChange}
                      placeholder={t(
                        "workspace.apiKeys.issueDialog.expirationPlaceholder"
                      )}
                      disabled={isSubmitting}
                      fromDate={tomorrow}
                    />
                  </FormControl>
                  <FormMessage />
                  <p className="text-xs text-slate-500 dark:text-slate-400 mt-1">
                    {t("workspace.apiKeys.issueDialog.expirationDescription")}
                  </p>
                </FormItem>
              )}
            />

            <DialogFooter>
              <Button
                type="button"
                variant="outline"
                onClick={() => onOpenChange(false)}
                disabled={isSubmitting}
              >
                {t("workspace.apiKeys.issueDialog.cancel")}
              </Button>
              <Button type="submit" disabled={isSubmitting}>
                {isSubmitting
                  ? t("workspace.apiKeys.issueDialog.issuing")
                  : t("workspace.apiKeys.issueDialog.issue")}
              </Button>
            </DialogFooter>
          </form>
        </Form>
      </DialogContent>
    </Dialog>
  )
}
